import { getDocs, runTransaction, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { matchDoc, matchesCol } from './matches';
import { tournamentDoc } from './tournaments';
import type { Match, MatchScore } from './types';

export interface SubmitResultInput {
  tournamentId: string;
  matchId: string;
  score: MatchScore;
}

/**
 * Помещает игрока в слот целевого матча. Если у матча задан deadSlot и его
 * живой слот после этого заполнен — матч схлопывается в walkover, и игрок
 * каскадно продвигается дальше. Мутирует `m` и связанные матчи в `byId`.
 * Возвращает множество id изменённых матчей.
 */
function placeAndCascade(
  byId: Map<string, Match>,
  targetId: string,
  slot: 'A' | 'B' | undefined,
  playerId: string,
  playerName: string | null,
  changed: Set<string>
): void {
  const m = byId.get(targetId);
  if (!m) return;

  let usedSlot: 'A' | 'B';
  if (slot === 'A' || slot === 'B') {
    usedSlot = slot;
  } else {
    usedSlot = m.playerAId === null ? 'A' : 'B';
  }
  if (usedSlot === 'A') {
    m.playerAId = playerId;
    m.playerAName = playerName;
  } else {
    m.playerBId = playerId;
    m.playerBName = playerName;
  }

  // Матч с мёртвым слотом: как только живой слот заполнен — walkover.
  if (m.deadSlot && m.status !== 'COMPLETED') {
    const liveSlot: 'A' | 'B' = m.deadSlot === 'A' ? 'B' : 'A';
    const livePlayerId = liveSlot === 'A' ? m.playerAId : m.playerBId;
    const livePlayerName = liveSlot === 'A' ? m.playerAName : m.playerBName;
    if (livePlayerId) {
      m.status = 'COMPLETED';
      m.score = { sets: [], winnerId: livePlayerId, summary: 'walkover' };
      changed.add(m.id);
      if (m.nextMatchId) {
        placeAndCascade(
          byId,
          m.nextMatchId,
          m.nextMatchSlot,
          livePlayerId,
          livePlayerName,
          changed
        );
      }
      return;
    }
  }

  if (m.playerAId && m.playerBId && m.status !== 'COMPLETED') {
    m.status = 'READY';
  }
  changed.add(m.id);
}

export async function submitResult(input: SubmitResultInput): Promise<void> {
  const { tournamentId, matchId, score } = input;

  // Список всех матчей читаем вне транзакции, чтобы получить id.
  const allSnap = await getDocs(matchesCol(tournamentId));
  const allIds = allSnap.docs.map((d) => d.id);

  await runTransaction(db, async (tx) => {
    // --- Фаза чтения: читаем все матчи турнира в память ---
    const byId = new Map<string, Match>();
    for (const id of allIds) {
      const snap = await tx.get(matchDoc(tournamentId, id));
      if (snap.exists()) byId.set(id, { ...snap.data() });
    }

    const match = byId.get(matchId);
    if (!match) {
      throw new Error('Матч не найден');
    }
    if (match.status === 'COMPLETED') {
      throw new Error('Матч уже сыгран');
    }
    if (score.winnerId !== match.playerAId && score.winnerId !== match.playerBId) {
      throw new Error('Победитель не участник этого матча');
    }

    // --- Применяем результат в памяти ---
    const changed = new Set<string>();
    match.score = score;
    match.status = 'COMPLETED';
    changed.add(match.id);

    const winnerName =
      score.winnerId === match.playerAId ? match.playerAName : match.playerBName;
    const loserId =
      score.winnerId === match.playerAId ? match.playerBId : match.playerAId;
    const loserName =
      loserId === match.playerAId ? match.playerAName : match.playerBName;

    // Победитель → nextMatchId (с каскадом byes).
    if (match.nextMatchId) {
      placeAndCascade(
        byId,
        match.nextMatchId,
        match.nextMatchSlot,
        score.winnerId,
        winnerName,
        changed
      );
    }

    // Проигравший → loserNextMatchId (Double Elimination, с каскадом byes).
    if (match.loserNextMatchId && loserId) {
      placeAndCascade(
        byId,
        match.loserNextMatchId,
        match.loserNextMatchSlot,
        loserId,
        loserName,
        changed
      );
    }

    // Завершение турнира: у матча нет продолжения и все остальные сыграны.
    let tournamentDone = false;
    if (!match.nextMatchId) {
      tournamentDone = [...byId.values()].every(
        (m) => m.id === matchId || m.status === 'COMPLETED'
      );
    }

    // --- Фаза записи ---
    for (const id of changed) {
      const m = byId.get(id)!;
      const updates: Record<string, unknown> = {
        playerAId: m.playerAId,
        playerBId: m.playerBId,
        playerAName: m.playerAName,
        playerBName: m.playerBName,
        status: m.status,
      };
      if (m.score) updates.score = m.score;
      tx.update(matchDoc(tournamentId, id), updates);
    }

    if (tournamentDone) {
      tx.update(tournamentDoc(tournamentId), {
        status: 'COMPLETED',
        updatedAt: serverTimestamp(),
      });
    }
  });
}
